/**
 * Éditeur "image de fond" pour le CMS — style carte large à la Sakinea.
 * Image en grand avec boutons overlay (Changer/Supprimer) + sliders dessous.
 *
 * Stocke 5 valeurs via les sous-keys :
 *   <key>                  URL de l'image
 *   <key>.opacity          0–100 (opacité de l'image)
 *   <key>.blur             0–20 (px de flou)
 *   <key>.overlay_color    hex (#RRGGBB)
 *   <key>.overlay_opacity  0–100 (opacité de l'overlay coloré)
 */
import { useRef } from "react";
import { Upload, Image as ImageIcon, Trash2 } from "lucide-react";

export default function BackgroundEditor({
    src = "",
    opacity = 100,
    blur = 0,
    overlayColor = "#0A3B2A",
    overlayOpacity = 0,
    fixed = false,
    onChange,
    onUpload,
    resolveAssetUrl, // ex: assetUrl from "@/lib/api"
}) {
    const fileRef = useRef(null);
    const update = (patch) => onChange?.({
        src, opacity, blur, overlayColor, overlayOpacity, fixed, ...patch,
    });
    const resolvedSrc = resolveAssetUrl ? resolveAssetUrl(src) : src;

    return (
        <div className="space-y-4">
            {/* Grande carte preview avec overlay buttons */}
            <div className="relative rounded-2xl overflow-hidden border-2 border-border bg-muted" style={{ aspectRatio: "16 / 7" }}>
                {resolvedSrc ? (
                    <img
                        src={resolvedSrc}
                        alt="background preview"
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{
                            opacity: opacity / 100,
                            filter: blur > 0 ? `blur(${blur}px)` : undefined,
                        }}
                        onError={(e) => { e.target.style.display = "none"; }}
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-10 w-10 mb-2 opacity-40" />
                        <p className="text-sm">Aucune image de fond — clique sur "Changer"</p>
                    </div>
                )}

                {/* Overlay coloré */}
                {resolvedSrc && overlayOpacity > 0 && (
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundColor: overlayColor,
                            opacity: overlayOpacity / 100,
                        }}
                    />
                )}

                {/* Texte de test pour vérifier contraste */}
                {resolvedSrc && (
                    <div className="absolute inset-0 flex items-center justify-center text-center px-4 pointer-events-none">
                        <div className="bg-black/30 backdrop-blur-sm rounded-xl px-4 py-3">
                            <p className="text-white/80 text-xs uppercase tracking-[0.2em]">Hero · exemple</p>
                            <h3 className="text-white font-display text-2xl font-black mt-1">Texte sur le fond</h3>
                            <p className="text-white/70 text-sm mt-1">Aperçu du contraste</p>
                        </div>
                    </div>
                )}

                {/* Label "Image de fond" en bas à gauche (style Sakinea) */}
                <div className="absolute bottom-3 left-3 max-w-[40%] pointer-events-none">
                    <div className="bg-black/60 backdrop-blur rounded-lg px-3 py-2">
                        <p className="text-white text-sm font-bold">Image de fond</p>
                        <p className="text-white/70 text-xs">Image panoramique pour l'arrière-plan</p>
                    </div>
                </div>

                {/* Boutons d'action */}
                <div className="absolute bottom-3 right-3 flex gap-2">
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-black/70 hover:bg-black/85 text-white backdrop-blur px-3 py-2 text-xs font-semibold transition-colors"
                    >
                        <Upload className="h-3.5 w-3.5" />
                        Changer
                    </button>
                    {resolvedSrc && (
                        <button
                            type="button"
                            onClick={() => update({ src: "" })}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-black/70 hover:bg-destructive text-white backdrop-blur px-3 py-2 text-xs font-semibold transition-colors"
                            title="Supprimer l'image de fond"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Supprimer
                        </button>
                    )}
                </div>

                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onUpload?.(f);
                        e.target.value = "";
                    }}
                />
            </div>

            {/* URL input */}
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={src || ""}
                    onChange={(e) => update({ src: e.target.value })}
                    placeholder="Chemin de l'image (laisser vide pour aucun fond)"
                    className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono"
                />
                {src && (
                    <button
                        type="button"
                        onClick={() => update({ src: "" })}
                        className="inline-flex items-center justify-center rounded-lg border border-border w-9 h-9 hover:border-destructive hover:text-destructive transition-colors"
                        title="Vider le champ"
                    >
                        ×
                    </button>
                )}
            </div>

            {/* Contrôles (sliders + overlay color) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl border border-border bg-muted/30 p-4">
                <SliderRow
                    label="Opacité image"
                    suffix="%"
                    value={opacity}
                    min={0} max={100} step={1}
                    onChange={(v) => update({ opacity: v })}
                />
                <SliderRow
                    label="Flou"
                    suffix="px"
                    value={blur}
                    min={0} max={20} step={0.5}
                    onChange={(v) => update({ blur: v })}
                />

                {/* Toggle "Fond fixe" (effet parallax) */}
                <div className="md:col-span-2 pt-3 border-t border-border">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <button
                            type="button"
                            onClick={() => update({ fixed: !fixed })}
                            className={`shrink-0 inline-flex items-center h-6 w-11 rounded-full transition-colors ${
                                fixed ? "bg-primary" : "bg-muted"
                            }`}
                            aria-pressed={fixed}
                        >
                            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                                fixed ? "translate-x-6" : "translate-x-1"
                            }`} />
                        </button>
                        <div>
                            <p className="text-sm font-semibold text-foreground">Fond fixe (effet parallax)</p>
                            <p className="text-xs text-muted-foreground">L'image reste fixe pendant que le contenu défile par-dessus.</p>
                        </div>
                    </label>
                </div>

                <div className="md:col-span-2 pt-3 border-t border-border space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Overlay coloré (assombrir / colorer le fond)</p>
                    <div className="flex items-center gap-3">
                        <input
                            type="color"
                            value={overlayColor || "#0A3B2A"}
                            onChange={(e) => update({ overlayColor: e.target.value })}
                            className="h-10 w-16 rounded-md border border-border bg-background cursor-pointer"
                        />
                        <input
                            type="text"
                            value={overlayColor || ""}
                            onChange={(e) => update({ overlayColor: e.target.value })}
                            placeholder="#0A3B2A"
                            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                        />
                    </div>
                    <SliderRow
                        label="Opacité overlay"
                        suffix="%"
                        value={overlayOpacity}
                        min={0} max={100} step={1}
                        onChange={(v) => update({ overlayOpacity: v })}
                    />
                </div>
            </div>
        </div>
    );
}


function SliderRow({ label, suffix = "", value, min, max, step, onChange }) {
    return (
        <div>
            <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
                <span className="tabular-nums font-mono text-muted-foreground">{Number(value).toFixed(step < 1 ? 1 : 0)}{suffix}</span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full accent-primary"
            />
        </div>
    );
}
